(() => {
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  }).format(Number(cents || 0) / 100);

  const swatches = {
    'antique cherry red': '#a6192e',
    'aquatic': '#2b8fa3',
    'ash': '#d6d4cf',
    'ash grey': '#d4d6d7',
    'athletic heather': '#a8aaad',
    'azalea': '#dc4f86',
    'bay': '#6f8f87',
    'black': '#111111',
    'blossom': '#e8a3b6',
    'blue': '#2d5b9f',
    'blue dusk': '#394b64',
    'blue jean': '#607b91',
    'blue spruce': '#2d5a55',
    'cement': '#a7a39d',
    'chambray': '#93afc1',
    'charcoal': '#46484b',
    'cherry red': '#c3203e',
    'coffee': '#60473a',
    'coral silk': '#ef8f83',
    'corn silk': '#fff0bd',
    'cornsilk': '#fff0bd',
    'dark chocolate': '#4b3029',
    'dark grey heather': '#4b4e52',
    'dark heather': '#55595d',
    'deep heather': '#5f666d',
    'deep royal': '#1e4da1',
    'espresso': '#3c2c25',
    'forest green': '#174b2a',
    'garnet': '#7a1f33',
    'gold': '#d89b00',
    'graphite': '#484c4f',
    'graphite heather': '#5e6265',
    'green': '#237a3f',
    'grey': '#8d9296',
    'heather dark grey': '#565a5e',
    'heather deep teal': '#246675',
    'heather forest': '#355c45',
    'heather gray': '#a8aaad',
    'heather military green': '#59634b',
    'heather navy': '#394f67',
    'heather royal': '#416fa9',
    'heather true royal': '#4476b5',
    'heliconia': '#db2d7d',
    'ivory': '#f2ead7',
    'kelly green': '#188447',
    'khaki': '#b6a47a',
    'light blue': '#a7d5eb',
    'light pink': '#f3c4d4',
    'lilac': '#b9a0cf',
    'maroon': '#6f263d',
    'midnight navy': '#182b49',
    'military green': '#596044',
    'moss': '#65734a',
    'natural': '#e7ddc8',
    'navy': '#1c2e4a',
    'oatmeal': '#d5c4a8',
    'olive': '#5c633c',
    'orange': '#f07f18',
    'pepper': '#4e4a47',
    'pink': '#ee8db4',
    'pitch black': '#0d0f10',
    'purple': '#5a2d82',
    'red': '#c8102e',
    'royal': '#1f5aa6',
    'safety green': '#69d55d',
    'safety pink': '#f38bbd',
    'sage': '#a6b39a',
    'sand': '#d0b994',
    'sapphire': '#0e75b8',
    'sky blue': '#79bfe6',
    'solid white blend': '#f7f7f4',
    'sport grey': '#b8babd',
    'tahiti blue': '#1794a5',
    'tan': '#caa574',
    'true navy': '#152b4f',
    'true royal': '#255bb5',
    'white': '#ffffff',
    'yellow': '#ffd20a',
  };

  const colorValue = (name) => {
    const key = String(name || '').toLowerCase().trim();
    if (swatches[key]) return swatches[key];
    const candidate = key.replace(/\s+/g, '');
    if (window.CSS?.supports?.('color', candidate)) return candidate;
    const families = [
      [/black/, '#111111'],
      [/navy/, '#1c2e4a'],
      [/blue|aquatic|sapphire|teal/, '#2876a8'],
      [/red|cherry|garnet|maroon/, '#9f263b'],
      [/green|sage|moss|olive/, '#4f7048'],
      [/purple|lilac/, '#77549a'],
      [/pink|blossom|azalea/, '#df7fa9'],
      [/orange/, '#f07f18'],
      [/yellow|gold/, '#e0ad00'],
      [/brown|chocolate|coffee|espresso/, '#5b4034'],
      [/grey|gray|heather|graphite|charcoal|cement/, '#777d80'],
      [/white|ivory|natural|corn|sand|tan|khaki|oatmeal/, '#e2d7bf'],
    ];
    return families.find(([pattern]) => pattern.test(key))?.[1] || '#6f7774';
  };

  class AdhyaCustomProduct {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-custom-form]');
      this.variants = JSON.parse(root.querySelector('[data-custom-variants]')?.textContent || '[]');
      this.color = '';
      this.size = '';
      this.busy = false;
      this.basePrice = Math.min(...this.variants.map((variant) => Number(variant.price || 0)));
      this.serviceIds = {
        front: root.getAttribute('data-service-front-printing'),
        back: root.getAttribute('data-service-back-printing'),
        pocket: root.getAttribute('data-service-pocket-printing'),
        background: root.getAttribute('data-service-background-removal'),
      };
      this.servicePrices = { front: 900, back: 900, pocket: 700, background: 100 };
      this.bind();
      this.paintSwatches();
      this.update();
    }

    bind() {
      this.root.querySelectorAll('[data-custom-color]').forEach((button) => {
        button.addEventListener('click', () => {
          this.color = button.dataset.customColor;
          if (!this.sizeAvailable(this.size)) this.size = '';
          const imageVariant = this.variants.find((variant) => variant.available && variant.color === this.color && variant.image);
          if (imageVariant?.image) this.setImage(imageVariant.image, `${this.root.dataset.productTitle} in ${this.color}`);
          this.update();
        });
      });

      this.root.querySelectorAll('[data-custom-size]').forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          this.size = button.dataset.customSize;
          this.update();
        });
      });

      this.root.querySelectorAll('[data-print-toggle]').forEach((input) => {
        input.addEventListener('change', () => {
          const row = input.closest('[data-print-row]');
          const upload = row?.querySelector('[data-print-upload]');
          const file = row?.querySelector('[data-artwork-file]');
          if (upload) upload.hidden = !input.checked;
          if (file) file.required = input.checked;
          this.update();
        });
      });

      this.root.querySelectorAll('[data-artwork-file]').forEach((input) => {
        input.addEventListener('change', () => this.update());
      });

      this.root.querySelector('[data-background-removal]')?.addEventListener('change', () => this.update());
      this.root.querySelector('[data-custom-quantity]')?.addEventListener('input', () => this.update());
      this.root.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.changeQuantity(-1));
      this.root.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.changeQuantity(1));
      this.form?.addEventListener('submit', (event) => this.submit(event));

      this.root.querySelectorAll('[data-custom-image]').forEach((button) => {
        button.addEventListener('click', () => {
          this.setImage(button.dataset.customImage, button.dataset.customAlt);
          this.root.querySelectorAll('[data-custom-image]').forEach((item) => {
            const active = item === button;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', String(active));
          });
        });
      });
    }

    paintSwatches() {
      this.root.querySelectorAll('[data-color-swatch]').forEach((swatch) => {
        swatch.style.backgroundColor = colorValue(swatch.dataset.colorSwatch);
        if (/white|natural|corn silk|ash grey|oatmeal/i.test(swatch.dataset.colorSwatch)) swatch.classList.add('is-light');
      });
    }

    setImage(src, alt) {
      const image = this.root.querySelector('[data-custom-main-image]');
      if (!image || !src) return;
      image.src = src;
      image.alt = alt || this.root.dataset.productTitle;
    }

    changeQuantity(delta) {
      const input = this.root.querySelector('[data-custom-quantity]');
      if (!input) return;
      input.value = String(Math.max(1, Math.min(99, Number(input.value || 1) + delta)));
      this.update();
    }

    quantity() {
      const input = this.root.querySelector('[data-custom-quantity]');
      const value = Math.max(1, Math.min(99, Number.parseInt(input?.value || '1', 10) || 1));
      if (input) input.value = String(value);
      return value;
    }

    locations() {
      return [...this.root.querySelectorAll('[data-print-toggle]:checked')].map((input) => input.value);
    }

    sizeAvailable(size) {
      if (!this.color || !size) return false;
      return this.variants.some((variant) => variant.available && variant.color === this.color && variant.size === size);
    }

    selectedVariant() {
      return this.variants.find((variant) => variant.available && variant.color === this.color && variant.size === this.size);
    }

    filesComplete() {
      return this.locations().every((location) => this.root.querySelector(`[data-artwork-file="${location}"]`)?.files?.length);
    }

    valid() {
      return Boolean(this.selectedVariant() && this.locations().length && this.filesComplete());
    }

    update() {
      const sizes = this.root.querySelectorAll('[data-custom-size]');
      sizes.forEach((button) => {
        const available = this.sizeAvailable(button.dataset.customSize);
        button.disabled = !available;
        button.classList.toggle('is-selected', button.dataset.customSize === this.size);
        button.setAttribute('aria-pressed', String(button.dataset.customSize === this.size));
      });

      this.root.querySelectorAll('[data-custom-color]').forEach((button) => {
        const active = button.dataset.customColor === this.color;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-pressed', String(active));
      });

      const selectedColor = this.root.querySelector('[data-selected-color]');
      const selectedSize = this.root.querySelector('[data-selected-size]');
      const sizeHint = this.root.querySelector('[data-size-hint]');
      if (selectedColor) selectedColor.textContent = this.color || 'Choose a color';
      if (selectedSize) selectedSize.textContent = this.size || (this.color ? 'Choose an available size' : 'Choose a color first');
      if (sizeHint) sizeHint.textContent = this.color ? 'Unavailable sizes are disabled for this color.' : 'Select a color to see available sizes.';

      const variant = this.selectedVariant();
      const garmentPrice = Number(variant?.price ?? this.basePrice);
      const quantity = this.quantity();
      const printPrice = this.locations().reduce((sum, location) => sum + this.servicePrices[location], 0);
      const background = this.root.querySelector('[data-background-removal]')?.checked ? this.servicePrices.background : 0;
      const total = (garmentPrice + printPrice) * quantity + background;
      const price = this.root.querySelector('[data-custom-price]');
      const totalEl = this.root.querySelector('[data-custom-total]');
      if (price) price.textContent = money(garmentPrice);
      if (totalEl) totalEl.textContent = money(total);

      const button = this.root.querySelector('[data-custom-submit]');
      const label = this.root.querySelector('[data-submit-label]');
      if (button) button.disabled = this.busy || !this.valid();
      if (label && !this.busy) {
        if (!this.color) label.textContent = 'Choose a color';
        else if (!this.size) label.textContent = 'Choose an available size';
        else if (!this.locations().length) label.textContent = 'Select at least one print location';
        else if (!this.filesComplete()) label.textContent = 'Upload artwork for every location';
        else label.textContent = `Add custom item - ${money(total)}`;
      }
    }

    message(text, error = false) {
      const element = this.root.querySelector('[data-custom-message]');
      if (!element) return;
      element.textContent = text;
      element.classList.toggle('is-error', error);
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy || !this.valid()) {
        this.message('Complete the color, size, print location, and artwork fields.', true);
        this.update();
        return;
      }

      const variant = this.selectedVariant();
      const locations = this.locations();
      const quantity = this.quantity();
      const background = Boolean(this.root.querySelector('[data-background-removal]')?.checked);
      const reference = `AS-${Date.now().toString(36).toUpperCase()}`;
      const rootUrl = window.Shopify?.routes?.root || '/';
      const formData = new FormData();
      formData.append('id', String(variant.id));
      formData.append('quantity', String(quantity));
      formData.append('properties[_Design reference]', reference);
      formData.append('properties[Print locations]', locations.map((item) => item[0].toUpperCase() + item.slice(1)).join(', '));
      formData.append('properties[Background removal]', background ? 'Yes' : 'No');
      const notes = this.root.querySelector('[data-custom-notes]')?.value.trim();
      if (notes) formData.append('properties[Print notes]', notes);
      locations.forEach((location) => {
        const file = this.root.querySelector(`[data-artwork-file="${location}"]`)?.files?.[0];
        formData.append(`properties[${location[0].toUpperCase() + location.slice(1)} artwork]`, file, file.name);
      });

      this.busy = true;
      this.message('Adding your custom item...');
      const label = this.root.querySelector('[data-submit-label]');
      if (label) label.textContent = 'Adding to cart...';
      this.update();

      let garmentLine;
      try {
        const garmentResponse = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        garmentLine = await garmentResponse.json();
        if (!garmentResponse.ok) throw new Error(garmentLine.description || 'The custom garment could not be added.');

        const serviceItems = locations.map((location) => ({
          id: Number(this.serviceIds[location]),
          quantity,
          properties: {
            '_Design reference': reference,
            'For custom item': this.root.dataset.productTitle,
            'Print location': location[0].toUpperCase() + location.slice(1),
          },
        }));
        if (background) {
          serviceItems.push({
            id: Number(this.serviceIds.background),
            quantity: 1,
            properties: {
              '_Design reference': reference,
              'For custom item': this.root.dataset.productTitle,
            },
          });
        }
        if (serviceItems.some((item) => !item.id)) throw new Error('Printing services are temporarily unavailable. Please contact support.');

        const serviceResponse = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: serviceItems }),
        });
        const serviceResult = await serviceResponse.json();
        if (!serviceResponse.ok) throw new Error(serviceResult.description || 'Printing services could not be added.');

        window.location.assign(`${rootUrl}cart`);
      } catch (error) {
        if (garmentLine?.key) {
          try {
            await fetch(`${rootUrl}cart/change.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: garmentLine.key, quantity: 0 }),
            });
          } catch (_) {
            // The visible error directs the customer to support if rollback also fails.
          }
        }
        this.busy = false;
        this.message(error.message || 'Could not add this custom item. Please try again.', true);
        this.update();
      }
    }
  }

  const mount = (scope = document) => {
    scope.querySelectorAll('[data-adhya-custom-product]:not([data-custom-mounted])').forEach((root) => {
      root.dataset.customMounted = 'true';
      new AdhyaCustomProduct(root);
    });

    scope.querySelectorAll('[data-custom-catalog]:not([data-catalog-mounted])').forEach((catalog) => {
      catalog.dataset.catalogMounted = 'true';
      const tabs = [...catalog.querySelectorAll('[data-custom-family]')];
      const panels = [...catalog.querySelectorAll('[data-custom-panel]')];
      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const family = tab.dataset.customFamily;
          tabs.forEach((item) => item.setAttribute('aria-selected', String(item === tab)));
          panels.forEach((panel) => { panel.hidden = panel.dataset.customPanel !== family; });
          tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        });
      });

      panels.forEach((panel) => {
        const toolbar = panel.querySelector('[data-custom-toolbar]');
        const grid = panel.querySelector('[data-custom-grid]');
        if (!toolbar || !grid) return;

        const cards = [...grid.querySelectorAll('[data-custom-card]')];
        const availability = toolbar.querySelector('[data-custom-availability]');
        const price = toolbar.querySelector('[data-custom-price-filter]');
        const sort = toolbar.querySelector('[data-custom-sort]');
        const resultCount = toolbar.querySelector('[data-custom-result-count]');
        const noResults = panel.querySelector('[data-custom-no-results]');

        const applyFilters = () => {
          const availabilityValue = availability?.value || 'all';
          const priceValue = price?.value || 'all';
          const sortValue = sort?.value || 'featured';

          const visible = cards.filter((card) => {
            const isAvailable = card.dataset.available === 'true';
            const cents = Number(card.dataset.price || 0);
            const matchesAvailability = availabilityValue === 'all'
              || (availabilityValue === 'available' && isAvailable)
              || (availabilityValue === 'sold-out' && !isAvailable);
            const matchesPrice = priceValue === 'all'
              || (priceValue === 'under-10' && cents < 1000)
              || (priceValue === '10-20' && cents >= 1000 && cents < 2000)
              || (priceValue === '20-plus' && cents >= 2000);
            return matchesAvailability && matchesPrice;
          });

          const title = (card) => String(card.dataset.title || '').toLocaleLowerCase();
          const featured = (card) => Number(card.dataset.featured || 0);
          const sorters = {
            'featured': (a, b) => featured(a) - featured(b),
            'best-selling': (a, b) => featured(a) - featured(b),
            'newest': (a, b) => Number(b.dataset.published || 0) - Number(a.dataset.published || 0),
            'title-asc': (a, b) => title(a).localeCompare(title(b)),
            'title-desc': (a, b) => title(b).localeCompare(title(a)),
            'price-asc': (a, b) => Number(a.dataset.price || 0) - Number(b.dataset.price || 0),
            'price-desc': (a, b) => Number(b.dataset.price || 0) - Number(a.dataset.price || 0),
          };
          visible.sort(sorters[sortValue] || sorters.featured);

          const visibleSet = new Set(visible);
          cards.forEach((card) => { card.hidden = !visibleSet.has(card); });
          [...visible, ...cards.filter((card) => !visibleSet.has(card))].forEach((card) => grid.append(card));
          if (resultCount) resultCount.textContent = `${visible.length} ${visible.length === 1 ? 'item' : 'items'}`;
          if (noResults) noResults.hidden = visible.length > 0;
        };

        [availability, price, sort].forEach((control) => control?.addEventListener('change', applyFilters));
        panel.querySelector('[data-custom-clear-filters]')?.addEventListener('click', () => {
          if (availability) availability.value = 'all';
          if (price) price.value = 'all';
          if (sort) sort.value = 'featured';
          applyFilters();
        });
        applyFilters();
      });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
})();
